import { ServiceApi } from "./ServiceApiWithLoading";
import TechnicalAssistance from '../model/TechnicalAssistance';
import { PaginationOptions, promiseGetBlob, promiseRequest, promiseSendFormData } from './ServiceApi';
import ComponentTechnicalAssistance from "../model/ComponentTechnicalAssistance";

export default class TechnicalAssistanceApi extends ServiceApi<TechnicalAssistance>  {

    constructor() {
        super('technicalAssistance');
        this.updateComponents = this.updateComponents.bind(this);
        this.updateDamagedCircuitBlocks = this.updateDamagedCircuitBlocks.bind(this);
        this.updateRelatedProblems = this.updateRelatedProblems.bind(this);
        this.saveAttachment = this.saveAttachment.bind(this);
        this.updateAttachmentObservation = this.updateAttachmentObservation.bind(this);
        this.deleteAttachment = this.deleteAttachment.bind(this);
        this.saveComment = this.saveComment.bind(this);
        this.deleteComment = this.deleteComment.bind(this);
        this.getBlobPdf = this.getBlobPdf.bind(this);
        this.updateCustomerContacts = this.updateCustomerContacts.bind(this);
        this.sendEmail = this.sendEmail.bind(this);
        this.getExportCsvList = this.getExportCsvList.bind(this);
        this.sendCustomerEmails = this.sendCustomerEmails.bind(this);
        this.sendTrackingPostOfficeEmails = this.sendTrackingPostOfficeEmails.bind(this);
        this.sendAvailableForPickupEmails = this.sendAvailableForPickupEmails.bind(this);
        this.sendProductReceivedEmails = this.sendProductReceivedEmails.bind(this);
    }

    async updateCustomerContacts(technicalAssistanceId: number | string, list: number[] | null) {
        var baseUrl = this.getBaseUrl();
        var result = await this.promiseWithLoading(promiseRequest(baseUrl + `${technicalAssistanceId}/customerContacts/`, 'PUT', list || undefined, true));
        return result;
    }

    async saveComment(technicalAssistanceId: number | string, comment: string, is_public: boolean, user_id: number) {
        var baseUrl = this.getBaseUrl();
        var result = await this.promiseWithLoading(promiseRequest(baseUrl + `${technicalAssistanceId}/comment/`, 'POST', { comment, user_id, is_public }, true));
        return result;
    }

    async deleteComment(commentTechnicalAssistanceId: number | string) {
        var baseUrl = this.getBaseUrl();
        var result = await this.promiseWithLoading(promiseRequest(baseUrl + `${commentTechnicalAssistanceId}/comment/`, 'DELETE', {}, true));
        return result;
    }

    async updateComponents(technicalAssistanceId: number | string, list: ComponentTechnicalAssistance[]) {
        var baseUrl = this.getBaseUrl();
        var result = await this.promiseWithLoading(promiseRequest(baseUrl + `${technicalAssistanceId}/components/`, 'PUT', list || undefined, true));
        return result;
    }

    async updateDamagedCircuitBlocks(technicalAssistanceId: number | string, list: number[] | null) {
        var baseUrl = this.getBaseUrl();
        var result = await this.promiseWithLoading(promiseRequest(baseUrl + `${technicalAssistanceId}/damagedCircuitBlocks/`, 'PUT', list || undefined, true));
        return result;
    }

    async updateRelatedProblems(technicalAssistanceId: number | string, list: number[] | null) {
        var baseUrl = this.getBaseUrl();
        var result = await this.promiseWithLoading(promiseRequest(baseUrl + `${technicalAssistanceId}/relatedProblems/`, 'PUT', list || undefined, true));
        return result;
    }

    async saveAttachment(data: File, technicalAssistanceId: number | string, description: string): Promise<any> {
        var baseUrl = this.getBaseUrl();
        const formData = new FormData();

        formData.append('description', description);
        formData.append('file', data);

        try {
            var result = await this.promiseWithLoading(promiseSendFormData(baseUrl + `${technicalAssistanceId}/attachment/`, 'POST', formData));
            return result;
        } catch {
            return null;
        }
    }

    async deleteAttachment(attachmentTechnicalAssistanceId: number | string) {
        var baseUrl = this.getBaseUrl();
        var result = await this.promiseWithLoading(promiseRequest(baseUrl + `${attachmentTechnicalAssistanceId}/attachment/`, 'DELETE', {}, true));
        return result;
    }

    async updateAttachmentObservation(attachmentTechnicalAssistanceId: number | string, description: string) {
        var baseUrl = this.getBaseUrl();
        var result = await this.promiseWithLoading(promiseRequest(baseUrl + `${attachmentTechnicalAssistanceId}/attachment/`, 'PUT', { description: description }, true));
        return result;
    }

    async getBlobPdf(ids: (number | string)[]): Promise<Blob | null> {
        var baseUrl = this.getBaseUrl();
        let result = await this.promiseWithLoading(this.promiseGetBlob(baseUrl + `exportPdf?ids=${ids.join(',')}`));
        return result;
    }

    async sendEmail(id: number | string) {
        var baseUrl = this.getBaseUrl();
        var result = await this.promiseWithLoading(promiseRequest(baseUrl + `${id}/sendEmail/`, 'POST', {}, true));
        return result;
    }

    async getExportCsvList(options: PaginationOptions<TechnicalAssistance>): Promise<Blob | null> {
        var url = this.getBaseUrl() + 'exportList';
        url = this.getUrl(url, options);
        return await this.promiseWithLoading(promiseGetBlob(url));
    }

    async sendCustomerEmails(ids: (number | string)[], type: number, emails: string[]) {
        var baseUrl = this.getBaseUrl();
        let result = await this.promiseWithLoading(promiseRequest(baseUrl + `sendCustomerEmails/`, 'POST', { ids, type, emails }, true));
        return result;
    }
    
    async sendTrackingPostOfficeEmails(ids: (number | string)[], tracking_post_office: string, emails: string[], invoice_number_out: string, service_invoice: string, local_pickup: boolean, declaration_content: boolean) {
        var baseUrl = this.getBaseUrl();
        let result = await this.promiseWithLoading(promiseRequest(baseUrl + `sendTrackingPostOfficeEmails/`, 'POST', { ids, tracking_post_office, emails, invoice_number_out, service_invoice, local_pickup, declaration_content }, true));
        return result;
    }
    
    async sendAvailableForPickupEmails(ids: (number | string)[], emails: string[]) {
        var baseUrl = this.getBaseUrl();
        let result = await this.promiseWithLoading(promiseRequest(baseUrl + `sendAvailableForPickupEmails/`, 'POST', { ids, emails }, true));
        return result;
    }

    async sendProductReceivedEmails(ids: (number | string)[], emails: string[]) {
        var baseUrl = this.getBaseUrl();
        let result = await this.promiseWithLoading(promiseRequest(baseUrl + `sendProductReceivedEmails/`, 'POST', { ids, emails }, true));
        return result;
    }
}

